import { AnsweringClient } from "../clients/answeringClient";
import { getCodeSession, isTokenExpired } from "./session";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Raised when the session cannot be renewed. `onSessionExpired` has already run
 * by the time this surfaces, so a terminal handler must swallow it: the session
 * ending is not a request failure, and reporting it as one tells the user the
 * wrong thing about what went wrong.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

export class AuthRetry {
  constructor(
    private readonly client: AnsweringClient,
    private readonly onSessionExpired: () => void
  ) {}

  /**
   * Runs `operation`, retrying it at most once with a refreshed access token.
   * The retry calls `operation` again instead of re-entering this method, so one
   * retry holds by construction: a route that refuses permanently — an expired
   * Instrument-Token answers 410, the permissions fence answers 403 — costs two
   * requests instead of looping until the session dies.
   *
   * Only 403 is retriable. A 410 marks a window no credential can reopen, and
   * these routes do not emit 401.
   *
   * A code session is the exception: it carries no refresh token, so its 403
   * has to be split by whether the token is still alive. An expired one means
   * the session ended; a live one means the backend refused on the merits — the
   * per-coleta scope guard answers 403 too — and that is an error to report,
   * not a session to restart. Without the split, deep-linking someone else's
   * coleta would bounce the person to the code screen as though their session
   * had ended.
   *
   * @throws SessionExpiredError when the refresh itself fails.
   * @throws the error the route reported, for anything not worth retrying.
   */
  public async run<T>(
    accessToken: string,
    operation: (token: string) => Promise<T>
  ): Promise<T> {
    try {
      return await operation(accessToken);
    } catch (error: any) {
      if (error?.response?.status !== 403) {
        throw error;
      }
      if (getCodeSession(accessToken) && !isTokenExpired(accessToken)) {
        throw error;
      }
      const tokens = await this.refresh();
      return operation(tokens.accessToken);
    }
  }

  private async refresh(): Promise<AuthTokens> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.onSessionExpired();
      throw new SessionExpiredError();
    }
    try {
      const response = await this.client.refreshToken(refreshToken);
      const tokens: AuthTokens = response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch {
      this.onSessionExpired();
      throw new SessionExpiredError();
    }
  }
}
